import React, { useContext, useState, useEffect } from 'react'
import ReactSVG from 'react-svg'
import classNames from 'classnames'

import { UseItemAnimationContext } from '../hooks/use-item-animation'
import OnboardingSetupLoading from '../components/onboarding-setup-loading'
import { useStyles } from './content-selection.styles'
import ContentGridItem from '../components/content-grid-item'
import { useContentSelection } from '../hooks/use-content-selection'
import Button from '../../../components/button'
import Loading from '../../../../images/neon/loading.svg'
import { useScreenWidth } from '../../../hooks/use-screen-width'
import { SCREEN_SIZE_BREAKPOINTS } from '../../../constants'

const MAX_SELECTED_CONTENT = 5
const MAX_LOAD_CONTENT = 10

/*
 * Onboarding Step 2:
 * Users can select between 0 and 5 contents before proceeding with the confirm button.
 */
const ContentSelection = () => {
  const styles = useStyles()
  const screenWidth = useScreenWidth()
  const { contents, confirmHandler, loading: contentsLoading } = useContentSelection()
  const [{ loading: animationLoading }] = useContext(UseItemAnimationContext)
  const [selectedList, setSelectedList] = useState([])
  const [visibleCount, setVisibleCount] = useState(0)

  /*
  ** Adjust visible contents count based on screen width.
  ** For mobile (<=480px), initially show 10 contents.
  ** Then load another 10 contents by clicking the Load More button.
  ** For larger screens, show all contents by default.
  */
  useEffect(() => {
    if (!contents.length) return

    if (screenWidth <= SCREEN_SIZE_BREAKPOINTS.MIN_480) {
      setVisibleCount(MAX_LOAD_CONTENT)
    } else {
      setVisibleCount(contents.length)
    }
  }, [contents.length, screenWidth])

  const selectHandler = (id) => {
    const isSelected = selectedList.indexOf(id) > -1

    if (isSelected) {
      const filterList = selectedList.filter(item => item !== id)
      setSelectedList(filterList)
      return
    }

    if (selectedList.length === MAX_SELECTED_CONTENT) return

    setSelectedList(selectedList.concat(id))
  }

  if (animationLoading) {
    return <OnboardingSetupLoading />
  }

  if (contentsLoading || visibleCount === 0) {
    return <div className={styles.container}><ReactSVG src={Loading} /></div>
  }

  const itemsToRender = contents.slice(0, visibleCount)
  const contentRemaining = contents.length - visibleCount
  const loadMoreButtonText = `LOAD MORE (${contentRemaining > MAX_LOAD_CONTENT ? MAX_LOAD_CONTENT : contentRemaining})`

  return (
    <>
      <div className={styles.title}>
        What are you interested in?
      </div>

      <div className={styles.subTitle}>
        Choose up to
        {' '}
        {MAX_SELECTED_CONTENT}
        {' '}
        shows or movies to help us tailor recommendations you’ll love
      </div>

      <div className={styles.container}>
        <div className={styles.grid}>
          {itemsToRender.map(item => {
            return (
              <ContentGridItem
                key={item.contentItemId}
                contentItemId={item.contentItemId}
                contentItem={item.contentItem}
                imageUri={item.image.uri}
                onSelected={contentItemId => selectHandler(contentItemId)}
                isSelected={selectedList.indexOf(item.contentItemId) > -1}
              />
            )
          })}
        </div>

        {contentRemaining > 0 && (
          <Button
            wrapperClassName={styles.buttonWrapper}
            className={classNames(styles.loadMoreButton, styles.button)}
            onClick={() => setVisibleCount(visibleCount + MAX_LOAD_CONTENT)}
            text={loadMoreButtonText}
            darkBGSecondary
          />
        )}

        <Button
          wrapperClassName={styles.buttonWrapper}
          className={styles.button}
          onClick={() => confirmHandler(selectedList)}
          text="START BROWSING"
        />
      </div>
    </>
  )
}

export default ContentSelection
